import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Box, Popover } from "@mui/material";
import { StriimButton, StriimTypography } from "@striim/striim-ui-v2";
import { AddCircleOutline, KeyboardArrowUp } from "@mui/icons-material";
import ReactFlow, {
    ReactFlowProvider,
    Node,
    Edge,
    Position,
    NodeChange,
    applyNodeChanges,
    Handle,
    NodeProps
} from "reactflow";
import { useAppSelector } from "src/stores/redux/hooks";
import { getAppFQN, getAppModel, getAppStatus } from "../helpers/selectors";
import MetaObjects from "core/services/metaStoreService/meta-objects";
import growl from "app/components/common/growl";
import NewSubflowDialog from "./new-subflow-dialog";
import navigateTo from "src/navigate-to";
import metaObjectConverter from "core/services/metaStoreService/metaobject-converter";
import { flowMapDropdownStyles } from "./add-flow.styles";
import { EdgeType } from "../flow/flow.constants";
import { NodeContent } from "../nodes/node.commons";
import { getNodeContainerStyles } from "../nodes/node.styles";
import { createEdgesFromComponentData } from "../helpers/edge.utils";
import { getExistingNodes } from "../helpers/flow.utils";
import { getAppComponents } from "../helpers/flow-data.utils";

interface FlowMapDropdownProps {
    flowName?: string;
}

const DropdownFlowNode = ({ data, sourcePosition, targetPosition, selected }: NodeProps) => (
    <Box
        sx={{
            ...getNodeContainerStyles(selected || false),
            borderRadius: "30px"
        }}
        data-test-id={`dropdown_node_${data.label}`}
    >
        <NodeContent data={data} hideDescription={true} />
        <Handle type="target" position={targetPosition || Position.Top} style={{ visibility: "hidden" }} />
        <Handle type="source" position={sourcePosition || Position.Bottom} style={{ visibility: "hidden" }} />
    </Box>
);

const DROPDOWN_NODE_TYPES = { FLOW: DropdownFlowNode };

const FlowMapDropdownContent: React.FC<{
    app: any;
    currentFlowId?: string;
    appStatus?: string | null;
    onFlowNodeClick?: (flowId: string | null) => void;
    onNewSubflow?: () => void;
    onClose: () => void;
}> = ({ app, currentFlowId, appStatus, onFlowNodeClick, onNewSubflow, onClose }) => {
    const [flowNodes, setFlowNodes] = useState<Node[]>([]);
    const [flowEdges, setFlowEdges] = useState<Edge[]>([]);

    const onNodesChange = useCallback(
        (changes: NodeChange[]) => setFlowNodes(nds => applyNodeChanges(changes, nds)),
        []
    );
    const appFQN = useAppSelector(getAppFQN);

    useEffect(() => {
        (async () => {
            if (!appFQN) return;

            const appComponents = await getAppComponents(appFQN);
            const existingNodes = await getExistingNodes(appFQN, appComponents, currentFlowId);
            if (!existingNodes) return;
            const flowNodes = existingNodes.filter(node => node.data.componentType === "FLOW");
            const edges = await createEdgesFromComponentData(appFQN, flowNodes, appComponents);

            setFlowNodes(flowNodes);
            setFlowEdges(edges);
        })();
    }, [appFQN, currentFlowId]);

    const handleFlowClick = useCallback(
        (_event: React.MouseEvent, node: Node) => {
            const flowId = node.id;
            const flowName = node.data?.label;

            if (!flowId || !flowName) return;

            const nsName = metaObjectConverter.getNamespace(flowId);
            const appNameFromFlow = app?.name ?? app?.get?.("name");
            const shortAppName = metaObjectConverter.getShortId(nsName, appNameFromFlow);

            navigateTo.Subflow(shortAppName, flowName, true);

            onFlowNodeClick?.(flowId);
            onClose();
        },
        [app, onFlowNodeClick, onClose]
    );

    const handleReloadApp = useCallback(() => {
        const nsName = app?.nsName ?? app?.get?.("nsName");
        const appNameValue = app?.name ?? app?.get?.("name");
        const shortAppName = metaObjectConverter.getShortId(nsName, appNameValue);

        navigateTo.App(shortAppName, true);

        onFlowNodeClick?.(null);
        onClose();
    }, [app, onFlowNodeClick, onClose]);

    const handleNewSubflow = useCallback(() => {
        onNewSubflow?.();
        onClose();
    }, [onNewSubflow, onClose]);

    const allowEditing = appStatus === "CREATED";

    const defaultEdgeOptions = useMemo(
        () => ({
            type: EdgeType,
            style: { strokeWidth: 2, stroke: "#BCC9D6" }
        }),
        []
    );

    return (
        <Box sx={flowMapDropdownStyles.dropdownContent}>
            <Box sx={flowMapDropdownStyles.header}>
                <StriimButton
                    onClick={handleReloadApp}
                    data-test-id="app-flow-reload"
                    // @ts-ignore
                    variant="text"
                >
                    {app?.name ?? app?.get?.("name")}
                </StriimButton>
                {allowEditing && (
                    <StriimButton onClick={handleNewSubflow} variant="primary" data-test-id="add-flow-button">
                        <Box sx={flowMapDropdownStyles.buttonContent}>
                            <AddCircleOutline style={{ fontSize: 18 }} />
                            Add Flow
                        </Box>
                    </StriimButton>
                )}
            </Box>
            <Box sx={flowMapDropdownStyles.flowMapContainer} id="flow-map-container">
                {flowNodes.length > 0 ? (
                    <ReactFlowProvider>
                        <ReactFlow
                            nodes={flowNodes}
                            edges={flowEdges}
                            onNodesChange={onNodesChange}
                            nodeTypes={DROPDOWN_NODE_TYPES}
                            defaultEdgeOptions={defaultEdgeOptions}
                            onNodeClick={handleFlowClick}
                            fitView
                            fitViewOptions={{ maxZoom: 1 }}
                            nodesDraggable={true}
                            nodesConnectable={false}
                            proOptions={{ hideAttribution: true }}
                            style={{ width: "100%", height: "100%" }}
                        />
                    </ReactFlowProvider>
                ) : (
                    <Box sx={flowMapDropdownStyles.emptyState}>
                        <StriimTypography variant="body2" color="greyscale.500">
                            No flows available
                        </StriimTypography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

const FlowMapDropdown: React.FC<FlowMapDropdownProps> = ({ flowName }) => {
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [isNewSubflowDialogOpen, setIsNewSubflowDialogOpen] = useState(false);
    const anchorEl = useRef<HTMLDivElement>(null);

    const app = useAppSelector(getAppModel);
    const appStatus = useAppSelector(getAppStatus);

    const appNsName = app?.nsName ?? app?.get?.("nsName");
    const appName = app?.name ?? app?.get?.("name");
    const currentFlowId = app?.id ?? app?.get?.("id");
    const currentFlowName = flowName || appName;
    const currentDeploymentPlanName = app?.deploymentPlanName ?? app?.get?.("deploymentPlanName");
    const showDeploymentInfo = appStatus !== "CREATED" && currentDeploymentPlanName;

    const handleClose = useCallback(() => {
        setDropdownOpen(false);
    }, []);

    const handleNewSubflow = useCallback(() => {
        setDropdownOpen(false);
        setIsNewSubflowDialogOpen(true);
    }, []);

    const handleNewSubflowSubmit = useCallback(
        async (flowName: string) => {
            try {
                const newFlow = new MetaObjects.FLOW.Model();
                newFlow.name = flowName;
                newFlow.nsName = appNsName;

                await newFlow.save({ flow: appName });

                const shortAppName = metaObjectConverter.getShortId(appNsName, appName);

                navigateTo.Subflow(shortAppName, flowName, true);
                setIsNewSubflowDialogOpen(false);

                const flowId = metaObjectConverter.getId(appNsName, "FLOW", flowName);
                const appId = metaObjectConverter.getId(appNsName, "APPLICATION", appName);

                growl.success(`Flow '${flowName}' created successfully`, "Success", flowId, appId);
            } catch (error) {
                const appId = metaObjectConverter.getId(appNsName, "APPLICATION", appName);

                growl.error(error.message || "Failed to create flow", "Error", undefined, appId);
            }
        },
        [appNsName, appName]
    );

    const handleNewSubflowCancel = useCallback(() => {
        setIsNewSubflowDialogOpen(false);
    }, []);

    useEffect(() => {
        const handleResize = () => {
            if (dropdownOpen) {
                setDropdownOpen(false);
            }
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [dropdownOpen]);

    return (
        <>
            <Box sx={flowMapDropdownStyles.dropdownWrapper} data-test-id="flow-dropdown-wrap">
                <StriimTypography variant="h3" color="primary.700">
                    Flow:
                </StriimTypography>
                <Box
                    ref={anchorEl}
                    sx={{
                        ...flowMapDropdownStyles.dropdownTrigger,
                        ...(dropdownOpen && { backgroundColor: "greyscale.200" })
                    }}
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    data-test-id="flow-dropdown-trigger"
                    id="flow-dropdown"
                >
                    <StriimTypography variant="body1" sx={flowMapDropdownStyles.appName}>
                        {appNsName}.{currentFlowName}
                    </StriimTypography>
                    {showDeploymentInfo && (
                        <>
                            <StriimTypography variant="body2" sx={flowMapDropdownStyles.deploymentInfo}>
                                deployed on
                            </StriimTypography>
                            <StriimTypography variant="body2" sx={flowMapDropdownStyles.deploymentInfo}>
                                {currentDeploymentPlanName}
                            </StriimTypography>
                        </>
                    )}
                    <KeyboardArrowUp sx={flowMapDropdownStyles.arrowIcon(dropdownOpen)} data-test-id="arrow" />
                </Box>
                <Popover
                    open={dropdownOpen}
                    anchorEl={anchorEl.current}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left"
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "left"
                    }}
                    slotProps={{
                        paper: {
                            sx: { ...flowMapDropdownStyles.dropdownPopover },
                            id: "flow-map-dropdown"
                        }
                    }}
                >
                    <FlowMapDropdownContent
                        app={app}
                        currentFlowId={currentFlowId}
                        appStatus={appStatus}
                        onNewSubflow={handleNewSubflow}
                        onClose={handleClose}
                    />
                </Popover>
            </Box>

            <NewSubflowDialog
                isOpen={isNewSubflowDialogOpen}
                namespace={appNsName}
                onSubmit={handleNewSubflowSubmit}
                onCancel={handleNewSubflowCancel}
            />
        </>
    );
};

export default FlowMapDropdown;
